import * as React from 'react';

export type ValidationPropType = {
  error?: string;
  validate?(value: string, validity: ValidityState): string | undefined;
};

export type ValidHtmlElements = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export function useValidation<TElement extends ValidHtmlElements>(
  elementRef: React.RefObject<TElement | null>,
  error?: string,
  validate?: (value: string, validity: ValidityState) => string | undefined,
) {
  const [invalid, setInvalid] = React.useState(false);
  const [validationMessage, setValidationMessage] = React.useState('');

  const checkInvalidation = React.useCallback(
    (element: TElement) => {
      const currentMessage = element.validationMessage;
      const nextCustomMessage = error ?? validate?.(element.value, element.validity) ?? '';

      if (currentMessage !== nextCustomMessage) {
        element.setCustomValidity(nextCustomMessage);
        return true;
      }

      return false;
    },
    [error, validate],
  );

  React.useEffect(() => {
    if (elementRef.current) {
      const element = elementRef.current;
      if (checkInvalidation(element)) {
        // `element.checkValidity()` will trigger `invalid` event if invalid
        // which will update and display the validation message. So in this
        // case we only run it if the `error`-prop is truly or if it already
        // displays a validation message.
        if (error || invalid) {
          setInvalid(!element.checkValidity());
        }
      }
    }
  }, [elementRef, error, invalid, checkInvalidation]);

  React.useEffect(() => {
    if (elementRef.current) {
      // We prefer not using React Syntetic events for onchange
      // because it would force the input to become controlled.
      // This is ok as long we don't modify state.
      const element = elementRef.current;
      const onInput = () => checkInvalidation(element);
      const event = element instanceof HTMLSelectElement ? 'change' : 'input';
      element.addEventListener(event, onInput);
      return () => element.removeEventListener(event, onInput);
    }
  }, [elementRef, checkInvalidation]);

  const updateValidation = React.useCallback(
    (element: TElement) => {
      checkInvalidation(element);
      setInvalid(!element.checkValidity());
    },
    [checkInvalidation],
  );

  const triggerInvalid = React.useCallback((element: TElement) => {
    setInvalid(true);
    setValidationMessage(element.validationMessage);
  }, []);

  return [invalid, validationMessage, updateValidation, triggerInvalid] as const;
}
